import type { GetAgencyProfileQuery, GetAgencyReviewsQuery } from '@sortlist-frontend/data-fetching/public-types';
import { TFunction } from '@sortlist-frontend/translation/ssr';
import { isNonEmptyString } from '@sortlist-frontend/utils';
import { ReactElement } from 'react';

import type { MediaAttachment } from '_backend/queries/shared/media-attachment';
import { getGumletImg } from '_core/media/get-media.utils';

type Params = {
  agencyName: string;
  reviewsCount: number;
  locality: string | null;
  reviewText?: string;
};

export const getPageTitle = (params: Params): string => {
  const { agencyName, reviewsCount, locality, reviewText = 'reviews' } = params;

  if (reviewsCount <= 0) {
    if (locality != null) {
      return `${agencyName}, ${locality} | Sortlist`;
    }
    return `${agencyName} | Sortlist`;
  } else if (reviewsCount > 0 && reviewsCount < 3) {
    return `${agencyName} (+ ${reviewText}) | Sortlist`;
  }
  return `${agencyName} (+ ${reviewsCount} ${reviewText}) | Sortlist`;
};

export type Section = {
  id: string;
  label: string;
  icon: ReactElement;
};

export type AgencyStats = {
  awardsCount: number;
  reviewsCount: number;
  reviewsScore: number;
  teamMembersCount: number;
  teamMembersCover: boolean;
  teamStory: boolean;
  worksCount: number;
};

export const getActiveSections = (
  sections: Section[],
  agencyStats: AgencyStats,
  agencyData: GetAgencyProfileQuery['agency'] | null | undefined,
): Section[] =>
  sections.filter((section) => {
    switch (section.id) {
      case 'portfolio':
        return agencyStats.worksCount > 0;
      case 'reviews':
        return true;
      case 'team':
        return agencyStats.teamMembersCover || agencyStats.teamStory;
      case 'awards':
        return agencyStats.awardsCount > 0;
      case 'services':
        return agencyData?.services?.totalCount != null && agencyData.services.totalCount > 0;
      case 'contact':
        return agencyData?.website != null || agencyData?.mainOffice?.address != null;
      case 'about':
        return true;
      case 'similar':
        return !agencyData?.paidMember;
      default:
        return false;
    }
  });

export const getSeoPageTitle = (
  top: string[] | string | null,
  name: string,
  locality: string,
  reviewsCount: number,
  t: TFunction,
) => {
  return top
    ? t('agency:profile.seo.metaTitle', { percentage: top })
    : getPageTitle({
        agencyName: name,
        reviewsCount: reviewsCount,
        locality,
        reviewText: t('agency:profile.seo.reviews'),
      });
};

export const getSeoPageImage = (top: string[] | string | null, cover: MediaAttachment | undefined | null) => {
  return cover && top ? getGumletImg(cover, { preferredWidth: 1200, mobile: false }) : undefined;
};

export const getAgencyStats = (agencyProfile: GetAgencyProfileQuery['agency'] | null | undefined) => {
  return {
    awardsCount: agencyProfile?.awards?.totalCount ?? 0,
    reviewsCount: agencyProfile?.reviews?.totalCount ?? 0,
    reviewsScore: agencyProfile?.reviews?.averageRating ?? 0,
    teamMembersCount: agencyProfile?.teamSize ?? 0,
    teamMembersCover: isNonEmptyString(agencyProfile?.teamPictureUrl),
    teamStory: isNonEmptyString(agencyProfile?.story),
    worksCount: agencyProfile?.works?.totalCount ?? 0,
  };
};
